// src/pages/SupervisoryAssessments/SupervisoryAverages.js
import React, { useEffect, useMemo, useState } from 'react';
import {
    CCard,
    CCardBody,
    CCardHeader,
    CModal,
    CModalHeader,
    CModalTitle,
    CModalBody,
    CModalFooter,
    CButton,
    CRow,
    CCol,
    CTable,
    CTableBody,
    CTableHeaderCell,
    CTableDataCell,
    CTableRow,
} from '@coreui/react';

import { apiClient } from 'services/Api';
import DataTableComponent from 'components/DataTableComponent';
import ToastNotification from 'components/globals/ToastNotification';
import { GROUP_FIELDS, getGroupsForDepartment, formatScore } from './questions';

// ---------- Modal com detalhe das médias ----------
const SupervisoryAveragesModal = ({ visible, onClose, record }) => {
    if (!record) return null;

    const { year, department_name } = record;
    const groups = getGroupsForDepartment(department_name);
    const allFields = GROUP_FIELDS.reduce((acc, group) => acc.concat(group), []);
    const overallValues = allFields
        .map((field) => Number(record.averages?.[field] ?? record[field]))
        .filter((val) => Number.isFinite(val));
    const overallAverage =
        overallValues.length > 0
            ? overallValues.reduce((acc, v) => acc + v, 0) / overallValues.length
            : null;
    const formatAverage = (value) => {
        if (value === null || value === undefined) return '—';
        const num = Number(value);
        if (Number.isNaN(num)) return '—';
        return num.toFixed(2);
    };

    return (
        <CModal
            visible={visible}
            onClose={onClose}
            alignment="center"
            size="xl"
            backdrop="static"
            scrollable
        >
            <CModalHeader>
                <CModalTitle>
                    Resultados – {department_name} ({year})
                </CModalTitle>
            </CModalHeader>
            <CModalBody style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                <CRow className="mb-3">
                    <CCol md={6}>
                        <strong>Ano:</strong> {year}
                        <div className="mt-1">
                            <strong>Média global:</strong> {formatAverage(overallAverage)}
                        </div>
                    </CCol>
                    <CCol md={6} className="text-end">
                        <span className="text-muted">Valores médios por questão</span>
                    </CCol>
                </CRow>

                {groups.map((group, groupIndex) => (
                    <CCard className="mb-4" key={group.title}>
                        <CCardHeader>
                            <h5 className="mb-0">{group.title}</h5>
                        </CCardHeader>
                        <CCardBody>
                            <CTable responsive hover>
                                <CTableBody>
                                    {group.questions.map((qText, qIndex) => {
                                        const fieldName = GROUP_FIELDS[groupIndex][qIndex];
                                        const value =
                                            record.averages?.[fieldName] ?? record[fieldName]; // compat: caso backend devolva averages[field]
                                        return (
                                            <CTableRow key={fieldName}>
                                                <CTableHeaderCell className="col-10">
                                                    {qText}
                                                </CTableHeaderCell>
                                                <CTableDataCell className="col-2 text-center">
                                                    {formatScore(value)}
                                                </CTableDataCell>
                                            </CTableRow>
                                        );
                                    })}
                                </CTableBody>
                            </CTable>
                        </CCardBody>
                    </CCard>
                ))}
            </CModalBody>
            <CModalFooter>
                <CButton color="secondary" variant="ghost" onClick={onClose}>
                    Fechar
                </CButton>
            </CModalFooter>
        </CModal>
    );
};

// ---------- Página principal ----------
const SupervisoryAverages = ({
    selectedYear: selectedYearProp,
    availableYears: availableYearsProp,
    onYearChange,
    showYearSelector = true,
}) => {
    const [allSummaries, setAllSummaries] = useState([]);
    const currentYear = new Date().getFullYear();

    // Local fallbacks (only used if parent does not control year/year list)
    const [selectedYearLocal, setSelectedYearLocal] = useState(currentYear);
    const [availableYearsLocal, setAvailableYearsLocal] = useState([]);
    const [toast, setToast] = useState(null);

    const selectedYear = selectedYearProp ?? selectedYearLocal;
    const availableYears = availableYearsProp ?? availableYearsLocal;

    const [avgModalOpen, setAvgModalOpen] = useState(false);
    const [activeAverage, setActiveAverage] = useState(null);

    // Fetch only once on mount.
    // (Avoid depending on selectedYearProp/availableYearsProp to prevent accidental re-fetch loops.)
    useEffect(() => {
        let cancelled = false;

        const fetchSupervisoryAverages = async () => {
            try {
                // Sem filtro de ano: obtemos todas as médias em que o utilizador foi avaliado
                const response = await apiClient.get('/supervisory-assessments/summary/');
                const data = response.data || [];
                if (cancelled) return;

                setAllSummaries(data);

                // Shared years range (2019 -> current year) unless parent provides it
                if (!availableYearsProp) {
                    const yearsArr = Array.from(
                        { length: currentYear - 2019 + 1 },
                        (_, i) => 2019 + i
                    ).sort((a, b) => b - a);
                    setAvailableYearsLocal(yearsArr);
                }

                // Only set local selected year when parent isn't controlling it
                if (selectedYearProp == null) {
                    setSelectedYearLocal((prev) => (Number.isFinite(prev) ? prev : currentYear));
                }
            } catch (error) {
                if (cancelled) return;
                console.error('Error fetching my supervisory averages:', error);
                setToast({
                    title: 'Avaliação de Chefias',
                    message: 'Erro ao carregar os resultados.',
                    type: 'error',
                });
                setAllSummaries([]);
                if (!availableYearsProp) setAvailableYearsLocal([currentYear]);
                if (selectedYearProp == null) setSelectedYearLocal(currentYear);
            }
        };

        fetchSupervisoryAverages();
        return () => {
            cancelled = true;
        };
        // Intentionally only on mount.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const openMyAverageModal = (row) => {
        setActiveAverage(row);
        setAvgModalOpen(true);
    };

    const closeMyAverageModal = () => {
        setAvgModalOpen(false);
        setActiveAverage(null);
    };

    // Filtrar linhas pelo ano seleccionado
    const filteredSummaries = useMemo(() => {
        if (!selectedYear) return allSummaries;
        return allSummaries.filter((r) => r.year === selectedYear);
    }, [allSummaries, selectedYear]);

    const columns = useMemo(
        () => [
            {
                name: 'Departamento',
                selector: (row) => row.department_name,
                sortable: true,
                cell: (row) => (
                    <button
                        type="button"
                        className="btn btn-link p-0 text-decoration-none"
                        onClick={() => openMyAverageModal(row)}
                    >
                        {row.department_name}
                    </button>
                ),
            },
        ],
        []
    );

    return (
        <>
            <ToastNotification toast={toast} setToast={setToast} />

            {allSummaries.length > 0 && (
                <CCard className="mb-4">
                    <CCardHeader>
                        <h4 className="mb-0">As suas Avaliações (médias por ano)</h4>
                    </CCardHeader>
                    <CCardBody>
                        <DataTableComponent
                            columns={columns}
                            data={filteredSummaries}
                            years={showYearSelector ? availableYears : null}
                            selectedYear={showYearSelector ? selectedYear : undefined}
                            onYearChange={
                                showYearSelector
                                    ? (newYear) => {
                                          const parsed =
                                              typeof newYear === 'number'
                                                  ? newYear
                                                  : parseInt(newYear, 10);

                                          if (onYearChange) {
                                              onYearChange(parsed);
                                          } else {
                                              setSelectedYearLocal(parsed);
                                          }
                                      }
                                    : null
                            }
                        />
                        <p className="mt-3 text-muted">
                            Clique no departamento para ver as pontuações médias por questão.
                        </p>
                    </CCardBody>
                </CCard>
            )}

            <SupervisoryAveragesModal
                visible={avgModalOpen}
                onClose={closeMyAverageModal}
                record={activeAverage}
            />
        </>
    );
};

export default SupervisoryAverages;
